import React, { useState } from 'react';
import { Home, Building, Palette, ClipboardList, Wrench, Shield, MessageCircle, ArrowRight, CheckCircle } from 'lucide-react';

const ServicesPage = () => {
  const [selectedService, setSelectedService] = useState(null);

  const handleWhatsAppClick = (serviceName = '') => {
    const message = serviceName 
      ? `Hello, I am interested in ${serviceName} services. Please provide more details.`
      : 'Hello, I would like to know more about NPSH Developers services.';
    window.open(`https://wa.me/919884611368?text=${encodeURIComponent(message)}`, '_blank');
  };

  const services = [
  {
    id: 1,
    icon: Home,
    title: "Residential Projects",
    description: "Apartments, Villas, Rowhouses, and Bungalows in Chennai and Trichy, delivered with premium amenities and modern construction.",
    features: [
      "Luxury Apartments (NATRA, NAVITA, NANDIKA, NAKSHATRA, NARMAD, VANDITA)",
      "Independent Villas & Row Houses",
      "Smart Home Integration (project-specific)",
      "Premium Interior Finishes",
      "Landscaped Gardens & Amenities",
      "Customer-centric Handover process"
    ],
    process: [
      "Site Analysis & Planning",
      "Architectural Design led by Blue Cube Architects",
      "Approval & Permits",
      "Construction, Quality Control, and Supervision",
      "Final Inspection & Handover"
    ],
    image: "pics/gallery/nivasam1.jpg" // Replace with your actual hosted image if possible
  },
  {
    id: 2,
    icon: Building,
    title: "Commercial Projects",
    description: "Design and execution of office complexes, retail spaces, and commercial buildings.",
    features: [
      "Flexible Commercial Layouts",
      "Modern Amenities",
      "Parking & Security Systems",
      "Quality Construction",
      "Experienced Project Managers",
      "On-Time Handover"
    ],
    process: [
      "Feasibility Study & Planning",
      "Commercial Design",
      "Approval & Permits",
      "Construction and Quality Assurance",
      "Handover"
    ],
    image: "pics/commercial projects.jpg"
  },
  {
    id: 3,
    icon: Palette,
    title: "Interior Design & Customization",
    description: "Customized interiors as per client requirements, including design and material selection.",
    features: [
      "Space Planning & Layout",
      "Material Selection",
      "Custom Furniture",
      "Color Schemes & Finishes",
      "Contemporary Designs",
      "Project Management"
    ],
    process: [
      "Client Consultation",
      "Design & Visualization",
      "Material Selection",
      "Execution & Installation",
      "Handover and Styling"
    ],
    image: "pics/interior design.png"
  },
  // ... If you offer dedicated services for Project Planning, Construction Management, and Maintenance, keep similar objects or merge into above according to your actual offering.
];


  const whyChooseUs = [
  {
    title: "Committed Since 2017",
    description: "Recognized as a leading construction company in Chennai—Tamilnadu since 2017",
    icon: ClipboardList
  },
  {
    title: "Wide Range of Projects",
    description: "Expertise in Row Houses, Villas, Bungalows, Flats, and Apartments",
    icon: Building
  },
  {
    title: "Reputed Team",
    description: "Team led by experienced professionals and architects, headed by Mr. N. Subramanian (Managing Director)",
    icon: Shield
  },
  {
    title: "Customer Satisfaction",
    description: "Utmost satisfaction achieved with many successful handovers and happy customers",
    icon: CheckCircle
  }
];


  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-yellow-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl lg:text-6xl font-bold text-yellow-600 mb-6">
              Our Services
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
             “Since opening our doors in 2017, we've been committed to our clients’ unique project needs and satisfaction. We use a comprehensive approach to provide a wide range of construction services in Chennai and beyond, bringing your dream projects to life with quality and care.”
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-gray-800">Complete Construction Solutions</h2>
              <p className="text-gray-600 leading-relaxed">
                From residential apartments to commercial complexes, from interior design to 
                ongoing maintenance - we provide end-to-end construction services that exceed expectations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => handleWhatsAppClick()}
                  className="inline-flex items-center px-8 py-4 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Get Free Consultation
                </button>
                <button
                  onClick={() => document.getElementById('services-grid').scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center px-8 py-4 border-2 border-yellow-600 text-yellow-600 hover:bg-yellow-600 hover:text-white font-semibold rounded-lg transition-all duration-300"
                >
                  Explore Services
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=600&h=400&fit=crop"
                alt="NPSH Construction Services"
                className="w-full h-96 object-cover rounded-2xl shadow-2xl border-4 border-yellow-100"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-yellow-600 mb-4">Why Choose NPSH Developers?</h2>
            <div className="w-24 h-1 bg-yellow-600 mx-auto rounded"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-2xl shadow-lg border-2 border-gray-100 hover:border-yellow-600 transition-all duration-300 text-center group"
                >
                  <div className="w-16 h-16 bg-yellow-100 group-hover:bg-yellow-600 rounded-2xl flex items-center justify-center mx-auto mb-6 transition-colors duration-300">
                    <IconComponent className="h-8 w-8 text-yellow-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4 group-hover:text-yellow-600 transition-colors duration-200">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Approved Loan Partners */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-yellow-600 mb-4">Home Loan Support from Leading Banks</h2>
            <div className="w-24 h-1 bg-yellow-600 mx-auto rounded"></div>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mt-4">
              We also support home loans from these trusted banks, making your dream home even more accessible.
            </p>
          </div>
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-row justify-center gap-6 w-full">
              {[
                { name: 'Canara', logo: 'pics/banks/canara.png' },
                { name: 'SBI', logo: 'pics/banks/sbi.png' },
                { name: 'HDFC', logo: 'pics/banks/hdfc.png' }
              ].map((bank) => (
                <div key={bank.name} className="bg-yellow-50 border-2 border-yellow-100 rounded-xl shadow-md w-48 h-32 flex flex-col items-center justify-center text-2xl font-bold text-yellow-700 hover:border-yellow-600 transition-all duration-300 text-center">
                  <img src={bank.logo} alt={bank.name + ' Bank Logo'} className="h-12 mb-2 object-contain" />
                  {bank.name} Bank
                </div>
              ))}
            </div>
            <div className="flex flex-row justify-center gap-6 w-full">
              {[
                { name: 'UNION', logo: 'pics/banks/union.png' },
                { name: 'PUNJAB NATIONAL', logo: 'pics/banks/punjab.png' },
                { name: 'INDIAN', logo: 'pics/banks/iob.png' }
              ].map((bank) => (
                <div key={bank.name} className="bg-yellow-50 border-2 border-yellow-100 rounded-xl shadow-md w-48 h-32 flex flex-col items-center justify-center text-2xl font-bold text-yellow-700 hover:border-yellow-600 transition-all duration-300 text-center">
                  <img src={bank.logo} alt={bank.name + ' Bank Logo'} className="h-12 mb-2 object-contain" />
                  {bank.name} Bank
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section id="services-grid" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-yellow-600 mb-4">Complete Service Portfolio</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From concept to completion, we handle every aspect of your construction project with precision and care.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = service.icon;
              return (
                <div
                  key={service.id}
                  className="group bg-white rounded-2xl shadow-lg border-2 border-gray-100 hover:border-yellow-600 transition-all duration-300 overflow-hidden cursor-pointer transform hover:scale-105 hover:shadow-xl"
                  onClick={() => setSelectedService(service)}
                >
                  <div className="aspect-video bg-gray-200 overflow-hidden relative">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  
                  <div className="p-8">
                    <div className="w-16 h-16 bg-yellow-100 group-hover:bg-yellow-600 rounded-2xl flex items-center justify-center mb-6 transition-colors duration-300">
                      <IconComponent className="h-8 w-8 text-yellow-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-800 mb-4 group-hover:text-yellow-600 transition-colors duration-200">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-yellow-600 font-medium">Learn More</span>
                      <ArrowRight className="h-5 w-5 text-yellow-600 group-hover:translate-x-2 transition-transform duration-200" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Detail Modal */}
      {selectedService && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedService(null)}>
          <div 
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border-2 border-yellow-600 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-2xl">
              <div className="flex justify-between items-start">
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mr-4">
                    <selectedService.icon className="h-8 w-8 text-yellow-600" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-yellow-600">{selectedService.title}</h2>
                    <p className="text-gray-600 mt-2">{selectedService.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedService(null)}
                  className="text-yellow-600 hover:text-yellow-700 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-8">
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-2xl font-bold text-yellow-600 mb-6">Key Features</h3>
                  <div className="space-y-3">
                    {selectedService.features.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-yellow-600 mb-6">Our Process</h3>
                  <div className="space-y-4">
                    {selectedService.process.map((step, index) => (
                      <div key={index} className="flex items-center">
                        <div className="w-8 h-8 bg-yellow-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-4 flex-shrink-0">
                          {index + 1}
                        </div>
                        <span className="text-gray-600">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 rounded-2xl p-8 text-center">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">
                  Ready to Get Started?
                </h3>
                <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                  Contact us today for a free consultation and detailed quote for your {selectedService.title.toLowerCase()} project.
                </p>
                <button
                  onClick={() => handleWhatsAppClick(selectedService.title)}
                  className="inline-flex items-center px-8 py-4 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Get Free Quote
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-600 to-yellow-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Let's Build Something Amazing Together
          </h2>
          <p className="text-xl text-yellow-100 mb-8 max-w-2xl mx-auto">
            Whether it's your dream home or commercial space, our expert team is ready to bring your vision to life with unmatched quality and precision.
          </p>
          <button
            onClick={() => handleWhatsAppClick()}
            className="inline-flex items-center px-8 py-4 bg-white text-yellow-600 font-bold rounded-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            <MessageCircle className="mr-2 h-6 w-6" />
            Start Your Project Today
          </button>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;